/**
 * Resize/DisplaySize behavior adapted from quill-image-resize 3.0.9.
 * The MIT License, Copyright (c) 2018
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
 * THE SOFTWARE.
 */
import Quill from "quill";

// Own only the two modules used by HTMLToolbar. Import the application's Quill
// instance instead of shipping a second editor inside a prebuilt plugin.
export default class ImageResize {
  private image?: HTMLImageElement;
  private overlay?: HTMLDivElement;
  private display?: HTMLDivElement;
  private drag?: { x: number; width: number; direction: number };
  private rootUserSelect = "";
  private bodyCursor = "";
  private readonly container: HTMLElement;

  constructor(private readonly quill: Quill) {
    this.container = quill.container;
    quill.root.addEventListener("click", this.onClick);
    quill.root.addEventListener("scroll", this.hide);
    quill.on("text-change", this.update);
    window.addEventListener("resize", this.update);
  }

  private onClick = (event: MouseEvent) => {
    if (!this.quill.isEnabled()) return;
    if (event.target instanceof HTMLImageElement) {
      event.preventDefault();
      if (event.target !== this.image) this.show(event.target);
    } else {
      this.hide();
    }
  };

  private show(image: HTMLImageElement) {
    this.hide();
    this.image = image;
    this.quill.setSelection(null);
    this.rootUserSelect = this.quill.root.style.userSelect;
    this.quill.root.style.userSelect = "none";

    const overlay = document.createElement("div");
    overlay.className = "ql-image-resize-overlay";
    Object.assign(overlay.style, {
      position: "absolute",
      boxSizing: "border-box",
      border: "1px dashed #444",
    });
    this.overlay = overlay;
    this.container.appendChild(overlay);

    const corners = ["nw", "ne", "se", "sw"] as const;
    for (const corner of corners) {
      const handle = document.createElement("div");
      handle.className = "ql-image-resize-handle";
      handle.dataset.corner = corner;
      Object.assign(handle.style, {
        position: "absolute",
        width: "12px",
        height: "12px",
        backgroundColor: "white",
        border: "1px solid #777",
        boxSizing: "border-box",
        opacity: "0.80",
        cursor:
          corner === "nw" || corner === "se" ? "nwse-resize" : "nesw-resize",
        [corner.includes("w") ? "left" : "right"]: "-6px",
        [corner.includes("n") ? "top" : "bottom"]: "-6px",
      });
      handle.addEventListener("mousedown", (event) => {
        if (event.button !== 0 || !this.image) return;
        event.preventDefault();
        this.drag = {
          x: event.clientX,
          width: this.image.width || this.image.naturalWidth,
          direction: corner.includes("w") ? -1 : 1,
        };
        this.bodyCursor = document.body.style.cursor;
        document.body.style.cursor = handle.style.cursor;
        document.addEventListener("mousemove", this.onDrag);
        document.addEventListener("mouseup", this.endDrag);
        window.addEventListener("blur", this.endDrag);
      });
      overlay.appendChild(handle);
    }

    const display = document.createElement("div");
    display.className = "ql-image-resize-size";
    Object.assign(display.style, {
      position: "absolute",
      font: "12px/1.0 Arial, Helvetica, sans-serif",
      padding: "4px 8px",
      textAlign: "center",
      backgroundColor: "white",
      color: "#333",
      border: "1px solid #777",
      boxSizing: "border-box",
      opacity: "0.80",
      cursor: "default",
      whiteSpace: "nowrap",
    });
    this.display = display;
    overlay.appendChild(display);
    image.addEventListener("load", this.update);
    document.addEventListener("mousedown", this.onOutsideMouseDown);
    document.addEventListener("keydown", this.onKeyDown, true);
    this.update();
  }

  private onOutsideMouseDown = (event: MouseEvent) => {
    if (
      event.target instanceof Node &&
      event.target !== this.image &&
      !this.overlay?.contains(event.target)
    )
      this.hide();
  };

  private onKeyDown = (event: KeyboardEvent) => {
    if (["Escape", "Tab"].includes(event.key)) {
      this.hide();
      return;
    }
    if (!this.image || !["Backspace", "Delete"].includes(event.key)) return;
    const blot = Quill.find(this.image);
    if (blot && !(blot instanceof Quill)) {
      event.preventDefault();
      this.quill.deleteText(this.quill.getIndex(blot), 1, Quill.sources.USER);
    }
    this.hide();
  };

  private onDrag = (event: MouseEvent) => {
    if (!this.image || !this.drag) return;
    const width = Math.max(
      1,
      Math.round(
        this.drag.width + (event.clientX - this.drag.x) * this.drag.direction,
      ),
    );
    const blot = Quill.find(this.image);
    if (blot && !(blot instanceof Quill)) {
      // Quill owns the width mutation and emits text-change, including when
      // resizing is the only edit before Save. Keep controls outside root HTML.
      this.quill.formatText(
        this.quill.getIndex(blot),
        1,
        "width",
        String(width),
        Quill.sources.USER,
      );
      this.update();
    }
  };

  private endDrag = () => {
    if (this.drag) {
      document.body.style.cursor = this.bodyCursor;
    }
    this.drag = undefined;
    document.removeEventListener("mousemove", this.onDrag);
    document.removeEventListener("mouseup", this.endDrag);
    window.removeEventListener("blur", this.endDrag);
  };

  private update = () => {
    if (!this.image || !this.overlay || !this.display) return;
    if (!this.quill.root.contains(this.image)) {
      this.hide();
      return;
    }
    const rect = this.image.getBoundingClientRect();
    const parent = this.container.getBoundingClientRect();
    Object.assign(this.overlay.style, {
      left: `${rect.left - parent.left - this.container.clientLeft + this.container.scrollLeft}px`,
      top: `${rect.top - parent.top - this.container.clientTop + this.container.scrollTop}px`,
      width: `${rect.width}px`,
      height: `${rect.height}px`,
    });
    this.display.textContent = `${Math.round(rect.width)} × ${Math.round(rect.height)}`;
    Object.assign(this.display.style, {
      right: "4px",
      bottom: "4px",
      left: "auto",
    });
    if (rect.width <= 120 || rect.height <= 30) {
      const size = this.display.getBoundingClientRect();
      Object.assign(this.display.style, {
        bottom: `-${size.height + 4}px`,
        right:
          this.image.style.float === "right" ? "auto" : `-${size.width + 4}px`,
        left:
          this.image.style.float === "right" ? `-${size.width + 4}px` : "auto",
      });
    }
  };

  private hide = () => {
    this.endDrag();
    if (this.image) {
      this.quill.root.style.userSelect = this.rootUserSelect;
      this.image.removeEventListener("load", this.update);
    }
    this.overlay?.remove();
    this.overlay = undefined;
    this.display = undefined;
    this.image = undefined;
    document.removeEventListener("mousedown", this.onOutsideMouseDown);
    document.removeEventListener("keydown", this.onKeyDown, true);
  };

  destroy() {
    this.hide();
    this.quill.root.removeEventListener("click", this.onClick);
    this.quill.root.removeEventListener("scroll", this.hide);
    this.quill.off("text-change", this.update);
    window.removeEventListener("resize", this.update);
  }
}
